import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { Case, ChecklistTemplate } from "../../db/schema";

interface ChecklistModalProps {
  templates: ChecklistTemplate[];
  onClose: () => void;
  onComplete: (newCase: Case) => void;
}

const ChecklistModal: React.FC<ChecklistModalProps> = ({
  templates,
  onClose,
  onComplete,
}) => {
  // Track which template is being created (null when idle)
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(
    null
  );

  const handleCreateCase = async (template: ChecklistTemplate) => {
    if (creatingTemplateId) return;
    setCreatingTemplateId(template.id);
    try {
      const response = await axios.post(
        "/api/cases/from-checklist",
        {
          template_id: template.id,
          checklist_data: {
            items: {},
            started_at: new Date().toISOString(),
          },
        },
        { withCredentials: true }
      );

      const newCase = response.data.case;
      toast.success("Checklist case created");
      onClose();
      onComplete(newCase);
    } catch (error) {
      console.error("Error creating case with checklist:", error);
      toast.error("Failed to create checklist case");
      setCreatingTemplateId(null);
    }
  };

  // While creating, show a full-screen processing wrapper similar to Editor's isProcessing view
  if (creatingTemplateId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-base-200">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-10 h-10 border-4 border-base-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-base-content mb-2">
            Creating Checklist Case
          </h3>
          <p className="text-sm text-base-content/60 mb-4">
            Setting up your checklist. You’ll be redirected to the case in a moment.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-4 text-left">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>What’s happening:</strong>
            </p>
            <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1">
              <li className="opacity-0 animate-fade-in [animation-fill-mode:forwards]">
                • Saving your selection
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:0.5s] [animation-fill-mode:forwards]">
                • Preparing checklist template
              </li>
              <li className="opacity-0 animate-fade-in [animation-delay:1s] [animation-fill-mode:forwards]">
                • Opening your case
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Template selection modal
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 bg-gray-500/80">
      <div className="bg-base-100 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Select Checklist Template</h3>
          <button onClick={onClose} className="text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-3">
          {templates.map((template) => (
            <button
              key={template.id}
              disabled={!!creatingTemplateId}
              onClick={() => handleCreateCase(template)}
              className="w-full p-4 border border-base-300 rounded-lg hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-left transition-colors disabled:opacity-50"
            >
              <div className="font-medium text-base-content">{template.name}</div>
              {template.description && (
                <div className="text-sm text-gray-600 dark:text-base-content/70 mt-1">
                  {template.description}
                </div>
              )}
              <div className="text-xs text-base-content/60 mt-2">
                {template.items.length} items •{" "}
                {template.items.filter((i) => i.required).length} required
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <Button onClick={onClose} variant="secondary" className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistModal;